import { useEffect, useMemo, useState } from 'react';

import {
  getDefaultVariantSelection,
  getProductVariantFields,
  getVariantSelectionEntries,
} from '../../util/productVariants';
import { getVariantStockSummary } from '../../util/variantStock';

/**
 * The variant selection ("Size: Medium", "Volume: 100ml") for one product page,
 * and how much of each version is left.
 *
 * The state lives above the order form because two things need it: the form
 * itself, and the "Add to basket" button that sits beside it. The form reports
 * every change back through onVariantSelectionChange.
 *
 * @param {Object} params
 * @param {Object} params.publicData listing.attributes.publicData
 * @param {Object} [params.metadata] listing.attributes.metadata
 * @param {Array} params.listingFieldConfigs config.listing.listingFields
 * @param {Object} params.categoryConfiguration config.categoryConfiguration
 * @param {number} [params.listingStock] listing.currentStock quantity
 * @returns {Object} variant fields, the current selection and its change
 *   handler, plus which options are sold out and how many of the chosen version
 *   are left
 */
export const useProductVariantSelection = params => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, listingStock } = params;

  const variantFields = getProductVariantFields({
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
  });

  // The listing is fetched after the first render, and its attributes are
  // denormalised into fresh objects, so the fields can't be memoized against
  // them. What matters is which options are on offer: while that string is
  // unchanged the shopper's selection — and the form built from it — is left be.
  const fieldsSignature = variantFields
    .map(field => `${field.key}:${field.options.map(option => option.value).join(',')}`)
    .join('|');

  /* eslint-disable react-hooks/exhaustive-deps */
  const productVariantFields = useMemo(() => variantFields, [fieldsSignature]);
  const initialVariantSelection = useMemo(() => getDefaultVariantSelection(variantFields), [
    fieldsSignature,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const [variantSelection, setVariantSelection] = useState(initialVariantSelection);

  // Apply the preselection once the listing (and with it its variants) is known.
  useEffect(() => {
    setVariantSelection(initialVariantSelection);
  }, [initialVariantSelection]);

  const variantEntries = getVariantSelectionEntries(productVariantFields, variantSelection);
  const isVariantSelectionComplete = variantEntries.length === productVariantFields.length;

  // Which combinations the brand actually holds. `selectedStock` is null when
  // the listing doesn't track per variation (so the listing's own stock rules,
  // exactly as before) or while the shopper is still choosing.
  const { optionAvailability, selectedStock, isTracked } = getVariantStockSummary({
    variantFields: productVariantFields,
    publicData,
    selection: variantSelection,
    listingStock,
  });

  return {
    productVariantFields,
    initialVariantSelection,
    variantSelection,
    onVariantSelectionChange: setVariantSelection,
    variantEntries,
    isVariantSelectionComplete,
    variantOptionAvailability: optionAvailability,
    selectedVariantStock: selectedStock,
    isVariantStockTracked: isTracked,
    // The chosen version is buyable when it is either not tracked at all or has
    // something left. An incomplete selection isn't sold out, just unfinished.
    isSelectedVariantOutOfStock: isVariantSelectionComplete && selectedStock === 0,
  };
};

export default useProductVariantSelection;
